import { API } from "../api";

const getBaseUrl = (guard = "staff") => {
  if (guard === "member") return "/members/notifications";
  if (guard === "investor") return "/investors/notifications";
  return "/cooperatives/notifications";
};

const parseListResponse = (responseData) => {
  const payload = responseData?.data;
  if (payload?.data && Array.isArray(payload.data)) {
    return {
      notifications: payload.data,
      pagination: {
        current_page: payload.current_page,
        total: payload.total,
        per_page: payload.per_page,
        last_page: payload.last_page,
      },
      meta: payload.meta || {},
    };
  }
  return {
    notifications: [],
    pagination: {},
    meta: {},
  };
};

const notificationExtendedApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: ({ page = 1, status = "all", limit = 20, guard = "staff", type = "" }) => {
        const typeParam = type ? `&type=${encodeURIComponent(type)}` : "";
        return `${getBaseUrl(guard)}?guard=${guard}&page=${page}&status=${status}&limit=${limit}&per_page=${limit}${typeParam}`;
      },
      transformResponse: parseListResponse,
      providesTags: (result) => {
        const notifications = result?.notifications || [];
        if (!notifications.length) {
          return [{ type: "Notification", id: "LIST" }];
        }
        return [
          { type: "Notification", id: "LIST" },
          ...notifications.map(({ id }) => ({ type: "Notification", id })),
        ];
      },
    }),

    getNotificationById: builder.query({
      query: (arg) => {
        const notificationId = typeof arg === "object" ? arg.notificationId : arg;
        const guard = typeof arg === "object" ? arg.guard || "staff" : "staff";
        return `${getBaseUrl(guard)}/${notificationId}?guard=${guard}`;
      },
      transformResponse: (responseData) => responseData?.data || null,
      providesTags: (result, err, arg) => {
        const notificationId = typeof arg === "object" ? arg.notificationId : arg;
        return [{ type: "Notification", id: notificationId }];
      },
    }),

    getUnreadCount: builder.query({
      query: (guard = "staff") => `${getBaseUrl(guard)}/unread-count?guard=${guard}`,
      transformResponse: (responseData) => responseData?.data?.unread_count ?? 0,
      providesTags: () => [{ type: "Notification", id: "UNREAD_COUNT" }],
    }),

    markAsRead: builder.mutation({
      query: (arg) => {
        const notificationId = typeof arg === "object" ? arg.notificationId : arg;
        const guard = typeof arg === "object" ? arg.guard || "staff" : "staff";
        return {
          url: `${getBaseUrl(guard)}/${notificationId}/mark-read?guard=${guard}`,
          method: "PATCH",
        };
      },
      invalidatesTags: (result, error, arg) => {
        const notificationId = typeof arg === "object" ? arg.notificationId : arg;
        return [
          { type: "Notification", id: notificationId },
          { type: "Notification", id: "UNREAD_COUNT" },
          { type: "Notification", id: "LIST" },
        ];
      },
    }),

    markAllAsRead: builder.mutation({
      query: (guard = "staff") => ({
        url: `${getBaseUrl(guard)}/mark-all-read?guard=${guard}`,
        method: "PATCH",
      }),
      invalidatesTags: [
        { type: "Notification", id: "UNREAD_COUNT" },
        { type: "Notification", id: "LIST" },
      ],
    }),

    bulkMarkAsRead: builder.mutation({
      query: ({ ids, guard = "staff" }) => ({
        url: `${getBaseUrl(guard)}/bulk-read?guard=${guard}`,
        method: "PATCH",
        body: { ids },
      }),
      invalidatesTags: [
        { type: "Notification", id: "UNREAD_COUNT" },
        { type: "Notification", id: "LIST" },
      ],
    }),

    bulkDeleteNotifications: builder.mutation({
      query: ({ ids, guard = "staff" }) => ({
        url: `${getBaseUrl(guard)}/bulk-delete?guard=${guard}`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: [
        { type: "Notification", id: "UNREAD_COUNT" },
        { type: "Notification", id: "LIST" },
      ],
    }),

    createNotification: builder.mutation({
      query: ({ body, guard = "staff" }) => ({
        url: `${getBaseUrl(guard)}/create?guard=${guard}`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "Notification", id: "UNREAD_COUNT" },
      ],
    }),

    deleteNotification: builder.mutation({
      query: (arg) => {
        const notificationId = typeof arg === "object" ? arg.notificationId : arg;
        const guard = typeof arg === "object" ? arg.guard || "staff" : "staff";
        return {
          url: `${getBaseUrl(guard)}/${notificationId}?guard=${guard}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result, error, arg) => {
        const notificationId = typeof arg === "object" ? arg.notificationId : arg;
        return [
          { type: "Notification", id: notificationId },
          { type: "Notification", id: "LIST" },
          { type: "Notification", id: "UNREAD_COUNT" },
        ];
      },
    }),

    getNotificationTypes: builder.query({
      query: (guard = "staff") => `${getBaseUrl(guard)}/types?guard=${guard}`,
      transformResponse: (responseData) => responseData?.data || [],
    }),

    getBranchNotifications: builder.query({
      query: ({ branchId, page = 1, guard = "staff" }) =>
        `${getBaseUrl(guard)}/branch/${branchId}?guard=${guard}&page=${page}`,
      transformResponse: (responseData) => responseData?.data || [],
      providesTags: (result, error, arg) => [
        { type: "Notification", id: "BRANCH" },
        { type: "Notification", id: arg.branchId },
      ],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useBulkMarkAsReadMutation,
  useBulkDeleteNotificationsMutation,
  useCreateNotificationMutation,
  useDeleteNotificationMutation,
  useGetNotificationTypesQuery,
  useGetBranchNotificationsQuery,
} = notificationExtendedApi;
